import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailTileCard,
    detailCta,
    type IslandMode,
    useIslandSection,
} from '~/ui-rocco/pages/detail-shared';
import type { DetailTile } from '~/ui-rocco/pages/detail-shared';
import { CONTENT_CAP_PADDED } from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { useProjectAccordionRows } from '../hooks/use-project-accordion-rows';

/**
 * P10 — Similar Projects. Category-wide projects (any professional) in the same
 * category, mirroring the product page's "More in {category}" block: a numbered
 * section divider, the giant two-tone heading + category breadcrumb, then a 3-up
 * grid of wide tiles — all on the dark editorial background. Self-hides when
 * there are no similar projects.
 */
/** Similar projects shown before the mobile "Load More" reveals the rest. */
const MOBILE_INITIAL_COUNT = 4;

export const SimilarProjectsSection = () => {
    const { project, similarProjects } = useLoaderData<typeof loader>();
    const { mobile } = useAppDisplayModeContext();
    const { rows } = useProjectAccordionRows();
    const [expanded, setExpanded] = useState(false);
    const category = project.Categories?.[0]?.PrettyTitle;

    const tiles: DetailTile[] = (similarProjects ?? [])
        .map((tile) => ({
            id: tile.id,
            link: tile.link,
            title: tile.title,
            images: tile.images,
            subtitle: tile.professional?.name,
            professional: {
                id: tile.professional?.id,
                name: tile.professional?.name,
            },
            professionalLink: tile.professional?.link,
            pinType: 'ProjectPin' as const,
        }))
        .filter((tile) => tile.images?.[0]?.src);
    // Mobile opens on a tidy 2×2 with a Load More to reveal the rest (the
    // category link / breadcrumb still carries the reader on to the full
    // directory beyond the loaded set).
    const visibleTiles =
        mobile && !expanded ? tiles.slice(0, MOBILE_INITIAL_COUNT) : tiles;
    const showLoadMore =
        mobile && !expanded && tiles.length > MOBILE_INITIAL_COUNT;

    // Magic Island: scrolling through this block hands the island a link out to
    // the category directory (the last breadcrumb), mirroring the product page's
    // "See more products in {category}" link.
    const breadcrumbs = project.Breadcrumbs;
    const categoryLink = breadcrumbs[breadcrumbs.length - 1]?.href;
    const similarMode = useMemo<IslandMode>(
        () => ({
            kind: 'link',
            label: `See more projects in ${category ?? 'this category'}`,
            to: categoryLink ?? '',
        }),
        [category, categoryLink],
    );
    // Desktop only: on mobile the persistent Book-a-Meeting pill stays put
    // rather than being swapped for this category link (which pulls the reader
    // off the enquiry path). Desktop has no purchase pill, so the link earns it.
    const islandRef = useIslandSection<HTMLElement>(similarMode, {
        enabled: !mobile && tiles.length > 0 && Boolean(categoryLink),
    });

    if (tiles.length === 0) return null;

    // Continues the page's numbered sequence: accordion rows → About Supplier
    // (rows + 1) → Similar Projects (rows + 2).
    const number = String(rows.length + 2).padStart(2, '0');

    return (
        <section
            ref={islandRef}
            className="bg-transparent text-white max-md:px-4 md:px-[3rem]"
        >
            <div className={CONTENT_CAP_PADDED}>
                <div className="border-t border-white/20 max-md:pt-3 md:pt-[1.062rem]">
                    <SectionLabel className="tracking-normal text-white max-md:text-[0.875rem]">
                        ({number} — Similar Projects)
                    </SectionLabel>
                </div>

                {/* Giant two-tone heading (Figma mirrors the product's 200px head);
                mobile drops to the shared 32px section-heading token. */}
                <h2 className="m-0 font-sans font-normal text-white max-md:mt-6 max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:mt-[6rem] md:text-[12.5rem] md:leading-[0.9] md:tracking-[-0.042em]">
                    More in <span className="text-[#a7a7a8]">{category}</span>{' '}
                    category
                </h2>

                {/* Category breadcrumb — last crumb parenthesised + medium. */}
                {project.Breadcrumbs.length > 0 ? (
                    <nav
                        aria-label="Category breadcrumb"
                        className="flex flex-wrap items-center gap-x-2 font-sans text-white max-md:mt-3 max-md:text-sm md:mt-[10rem] md:text-[1.375rem]"
                    >
                        {project.Breadcrumbs.map((crumb, index) => {
                            const isLast =
                                index === project.Breadcrumbs.length - 1;
                            return (
                                <span
                                    key={crumb.href}
                                    className="flex items-center gap-x-2"
                                >
                                    <RoccoLink
                                        to={crumb.href}
                                        className={cn(
                                            'transition-opacity hover:opacity-60',
                                            isLast && 'font-medium',
                                        )}
                                    >
                                        {isLast
                                            ? `(${crumb.label})`
                                            : crumb.label}
                                    </RoccoLink>
                                    {isLast ? null : (
                                        <span aria-hidden="true">/</span>
                                    )}
                                </span>
                            );
                        })}
                    </nav>
                ) : null}

                <div className="grid grid-cols-2 max-md:mt-6 max-md:gap-x-3.5 max-md:gap-y-[3.6875rem] md:mt-[2.125rem] md:grid-cols-3 md:gap-x-[1.5rem] md:gap-y-[6.25rem]">
                    {visibleTiles.map((tile) => (
                        <DetailTileCard
                            key={tile.id}
                            tile={tile}
                            theme="dark"
                            mediaClassName="md:aspect-[37/27]"
                        />
                    ))}
                </div>

                {showLoadMore ? (
                    <div className="mt-10 flex justify-center md:hidden">
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className={detailCta(
                                'filled-dark',
                                'h-[3.125rem] w-full',
                            )}
                        >
                            Load More
                        </button>
                    </div>
                ) : null}
            </div>
        </section>
    );
};
